// Structured evidence layer — Mode B (laboratory voice). Nothing here is estimated:
// a field reads "Not published" until a verified value is supplied.

export const NOT_PUBLISHED = "Not published";

export const STATUS = {
  PROVEN: {
    id: "PROVEN",
    label: "Proven",
    summary: "Physically demonstrated, tested, installed or otherwise substantiated.",
  },
  VALIDATION: {
    id: "VALIDATION",
    label: "Validation",
    summary: "Under engineering testing or prototyping. Behaviour observed; tolerances not yet characterised.",
  },
  FRONTIER: {
    id: "FRONTIER",
    label: "Frontier",
    summary: "A research question. Unexplored territory — not an established capability.",
  },
};

export const MEDIA_LABELS = {
  RAW_VIDEO: "Raw video",
  UNEDITED_TEST: "Unedited test",
  REAL_INSTALLATION: "Real installation",
  LAB_TEST: "Lab test",
  MICROSCOPE: "Microscope",
  PROTOTYPE: "Prototype",
  CONCEPT: "Concept",
  RENDERING: "Rendering",
  AI_VISUALIZATION: "AI visualization",
  SIMULATION: "Simulation",
};

// Brand hierarchy — canonical names already approved on the site.
export const TAXONOMY = [
  { name: "PRISMPIXEL™", role: "The optical material platform. A formulated coating that turns a surface into a canvas for projected light." },
  { name: "PRISMPIXEL GLASS™", role: "Transparent formulation. Keeps a window a window.", to: "/patron" },
  { name: "PRISMPIXEL GLOW™", role: "Opaque, high-luminance formulation for stages, studios and signage.", to: "/patron" },
  { name: "PRISMPIXEL CREATORS", role: "The creative, research and collaboration ecosystem around the material.", to: "/commons" },
  { name: "LUMEN8 LABORATORIES", role: "The laboratory responsible for developing PrismPixel.", to: "/about" },
];

// Lab test log. Media are the existing laboratory videos on the homepage; quantitative values are unpublished.
export const LAB_TESTS = [
  {
    id: "LT-001",
    anchor: "video-pie-lid",
    title: "Projection onto compound curvature",
    date: NOT_PUBLISHED,
    material: "PrismPixel™ coating",
    formulation: NOT_PUBLISHED,
    substrate: "Compound-curved surface · material not published",
    projector: NOT_PUBLISHED,
    output: NOT_PUBLISHED,
    throw: NOT_PUBLISHED,
    ambient: NOT_PUBLISHED,
    viewing: NOT_PUBLISHED,
    objective: "Map a projected image onto arbitrary 3D geometry.",
    observed: "Projected text remains sharp and legible across compound curves.",
    status: "PROVEN",
    media: ["LAB_TEST", "RAW_VIDEO"],
    notes: "Demonstrated on video. Quantitative results not published.",
  },
  {
    id: "LT-002",
    anchor: "video-lab",
    title: "Laboratory validation — transparent substrate",
    date: NOT_PUBLISHED,
    material: "PrismPixel Glass™",
    formulation: NOT_PUBLISHED,
    substrate: "Transparent substrate · not published",
    projector: NOT_PUBLISHED,
    output: NOT_PUBLISHED,
    throw: NOT_PUBLISHED,
    ambient: NOT_PUBLISHED,
    viewing: NOT_PUBLISHED,
    objective: "Form a projected image on a transparent surface while retaining see-through clarity.",
    observed: NOT_PUBLISHED,
    status: "PROVEN",
    media: ["LAB_TEST", "RAW_VIDEO"],
    notes: "Demonstrated on video. Measured transmittance and contrast not published.",
  },
  {
    id: "LT-003",
    anchor: "video-lab-two",
    title: "Glow™ on transparent Mylar — off-axis rear projection",
    date: NOT_PUBLISHED,
    material: "PrismPixel Glow™",
    formulation: NOT_PUBLISHED,
    substrate: "Transparent Mylar (PET) film",
    projector: NOT_PUBLISHED,
    output: NOT_PUBLISHED,
    throw: NOT_PUBLISHED,
    ambient: NOT_PUBLISHED,
    viewing: "Extreme off-axis (angle not published)",
    objective: "Rear-project onto a flexible, rollable coated film and view from extreme off-axis positions.",
    observed: "Image holds at extreme off-axis viewing; coated film remains flexible and rollable.",
    status: "PROVEN",
    media: ["LAB_TEST", "RAW_VIDEO"],
    notes: "Demonstrated on video. Ambient lux at test not published.",
  },
  {
    id: "LT-004",
    anchor: "video-liquid-pixel",
    title: "First successful formulation — LiquidPixel™",
    date: "Not published · ≈ year 5½ of the programme",
    material: "LiquidPixel™ (formulation name)",
    formulation: "LiquidPixel™",
    substrate: NOT_PUBLISHED,
    projector: NOT_PUBLISHED,
    output: NOT_PUBLISHED,
    throw: NOT_PUBLISHED,
    ambient: NOT_PUBLISHED,
    viewing: NOT_PUBLISHED,
    objective: "First formulation to form a usable projected image.",
    observed: "Recorded as the first success of the programme.",
    status: "PROVEN",
    media: ["LAB_TEST", "RAW_VIDEO"],
    notes: "Historical record. Measurements not published.",
  },
];

export const LAB_FIELDS = [
  ["date", "Date"], ["material", "Material"], ["formulation", "Formulation"], ["substrate", "Substrate"],
  ["projector", "Projector"], ["output", "Projector output"], ["throw", "Throw distance"], ["ambient", "Ambient lighting"],
  ["viewing", "Viewing position"],
];

// Specification sheet — every value is withheld until verified. These are not estimates.
export const SPEC_SHEET = [
  { key: "coating-thickness", label: "Coating thickness", unit: "µm" },
  { key: "transparency", label: "Luminous transmittance (Glass™)", unit: "%" },
  { key: "viewing-angle", label: "Viewing angle", unit: "°" },
  { key: "projector-compatibility", label: "Projector compatibility", unit: "lm · throw ratio" },
  { key: "substrates", label: "Supported substrates", unit: "list" },
  { key: "ambient-conditions", label: "Ambient illumination test conditions", unit: "lux" },
].map((row) => ({ ...row, value: NOT_PUBLISHED }));

// Target specification — supplied by the laboratory, 2026-10. Status THEORETICAL: derived from optical physics
// principles, formulation theory and laboratory methodology; target performance at commercial production scale. Not measured.
export const THEORETICAL = "Theoretical";
export const TARGET_SPECS = {
  note: "All specifications derived from optical physics principles, formulation theory, and laboratory methodology. Values represent target performance at commercial production scale.",
  glass: {
    title: "PrismPixel Glass™",
    rows: [
      ["Coating thickness", "15 ± 3", "µm"], ["Luminous transmittance", "92", "%"], ["Viewing angle", "±50° (H) / ±35° (V)", "°"], ["Hotspot extinction", "99.0", "%"],
      ["Screen gain", "1.7", "×"], ["MTF @ 10 lp/mm", "0.82", "—"], ["Colour fidelity (ΔE*ab)", "<0.12", "—"], ["Forward-to-backward ratio", "5.8", ":1"],
      ["Projector compatibility", "500–3000", "lm"], ["Throw ratio range", "1.2–3.5", "—"], ["Supported substrates", "Glass, Acrylic, Polycarbonate, Film", "list"],
      ["Ambient illumination test condition", "100", "lux"], ["Operating temperature", "−20 to +60", "°C"], ["UV resistance (ISO 11341)", "500 hours", "hrs"],
      ["Humidity resistance (ASTM D2247)", "1000 hours @ 85°C/85% RH", "hrs"], ["Adhesion (ASTM D3359)", "5B (Perfect)", "—"],
    ],
  },
  glow: {
    title: "PrismPixel Glow™",
    rows: [
      ["Coating thickness", "15 ± 3", "µm"], ["Luminous transmittance", "88", "%"], ["Viewing angle", "±90° (H) / ±60° (V)", "°"], ["Full front axis", "Yes", "—"],
      ["Hotspot extinction", "98.5", "%"], ["Screen gain", "1.5", "×"], ["MTF @ 10 lp/mm", "0.80", "—"], ["Colour fidelity (ΔE*ab)", "<0.12", "—"],
      ["Forward-to-backward ratio", "5.5", ":1"], ["Projector compatibility", "300–2500", "lm"], ["Throw ratio range", "1.2–3.5", "—"], ["Flexibility", "Fully rollable", "—"],
      ["Minimum bend radius", "30", "cm"], ["Supported substrates", "Polyester film (PET/Mylar), Glass, Acrylic", "list"], ["Ambient illumination test condition", "100", "lux"],
      ["Operating temperature", "−20 to +60", "°C"], ["UV resistance (ISO 11341)", "500 hours", "hrs"], ["Humidity resistance (ASTM D2247)", "1000 hours @ 85°C/85% RH", "hrs"], ["Adhesion (ASTM D3359)", "5B (Perfect)", "—"],
    ],
  },
  physics: {
    title: "Optical physics",
    columns: ["Parameter", "Value", "Unit", "Interpretation"],
    rows: [
      ["Optical depth (direct beam)", "0.33", "τ", "Single-scattering"], ["Optical depth (effective/diffuse)", "4.6", "τ", "Boundary regime operation"],
      ["Photons unscattered (0 events)", "71.9", "%", "Direct transmission"], ["Photons single-scattered (1 event)", "23.8", "%", "Directional preservation"],
      ["Photons double-scattered (2 events)", "3.9", "%", "Low blur / MTF preservation"], ["Mie scattering efficiency (PS @ 600 nm)", "2.34", "Q_sca", "Resonance enhancement"],
      ["Asymmetry parameter (forward-bias)", "0.68", "g", "Henyey-Greenstein"], ["GRIN gradient magnitude", "−6.0×10⁻⁴", "dn/dz (µm⁻¹)", "Refractive index slope"],
      ["GRIN surface layer (0–3 µm)", "1.512–1.515", "n", "PMSQ enriched"], ["GRIN bulk layer (12–15 µm)", "1.505", "n", "Equilibrium concentration"],
      ["GRIN decay length", "4–5", "µm", "Exponential falloff"], ["Cure phase 1 duration", "0–60", "min", "Slow viscosity rise (50 mPa·s/min)"],
      ["Cure phase 2 duration", "60–120", "min", "Moderate rise (33 mPa·s/min)"], ["Cure phase 3 duration", "120–360", "min", "Rapid rise (>500 mPa·s/min)"],
      ["Gel point (particle arrest)", "110 ± 10", "min", "Critical segregation freeze"], ["Refractive index target", "1.5090", "—", "Optimised for glass/acrylic"],
      ["Thermal stability (Tg)", "85", "°C", "Safe operating margin"], ["Photon transit time", "50–100", "ns", "Zero ghosting / temporal response"],
    ],
  },
  durability: {
    title: "Environmental durability",
    columns: ["Test", "Duration", "Condition", "Expected result"],
    rows: [
      ["UV exposure", "500 hours", "ISO 11341 xenon arc, 120 W/m², 63°C", "<0.15 ΔE*ab colour shift"], ["Thermal ageing", "1000 hours", "60°C continuous", "<1% optical drift"],
      ["Humidity resistance", "1000 hours", "85°C / 85% RH", "<0.4% transmittance change"], ["Salt spray", "1000 hours", "5% NaCl, ASTM B117", "Zero substrate corrosion"],
      ["Abrasion resistance", "1000 cycles", "Taber CS-17, 1000 g load", "Weight loss <10 mg"],
    ],
  },
};

// "What do you want to do with light?" — each path leads to information plus a tailored collaboration intent.
export const CONTACT_EMAIL = "charlie@prismpixelcreators.com";
export const AUDIENCES = [
  { id: "create", verb: "Create", who: "Artists, designers, immersive creators.", to: "/archive", toLabel: "Open the Inverse Archive", role: "Artist", interests: ["Artistic collaboration"] },
  { id: "build", verb: "Build", who: "Architects, fabricators, experiential designers.", to: "/technology#materials", toLabel: "Material system", role: "Architect", interests: ["Installation"] },
  { id: "integrate", verb: "Integrate", who: "Display companies, projection companies, OEMs.", to: "/technology#how-it-works", toLabel: "How it works", role: "Integrator", interests: ["OEM integration"] },
  { id: "research", verb: "Research", who: "Universities, scientists, engineers.", to: "/lab", toLabel: "The laboratory record", role: "Researcher", interests: ["Research collaboration", "Material testing"] },
  { id: "specify", verb: "Specify", who: "Architects, consultants, integrators.", to: "/samples", toLabel: "Material samples", role: "Architect", interests: ["Glass", "Material testing"] },
  { id: "partner", verb: "Partner", who: "Strategic partners, investors, manufacturers.", to: "/about", toLabel: "Lumen8 Laboratories", role: "Investor", interests: ["Licensing"] },
];

export const mailto = (subject, body = "") =>
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ""}`;

// Collaboration form vocabularies — mirrored in backend/collaborate.py.
export const ROLES = ["Artist", "Architect", "Designer", "Researcher", "Engineer", "Integrator", "Manufacturer", "Brand", "Institution", "Investor", "Other"];
export const INTERESTS = ["Glass", "Glow", "Research collaboration", "Installation", "Material testing", "Licensing", "OEM integration", "Artistic collaboration", "Other"];
export const SETTINGS = ["Indoor", "Outdoor", "Both", "Not sure"];

// Archive categories — consolidated from the briefs' fine-grained sectors (kept as tags).
const SECTOR_CATEGORY = {
  "Luxury Retail": "Retail", "Flagship Retail": "Retail", "Footwear": "Retail", "Fragrance & Beauty": "Retail",
  "Fine Jewelry": "Retail", "Watches & Jewelry": "Retail", "Horology": "Retail", "Luxury Accessories": "Retail",
  "Luxury Brands": "Retail", "Precious Metals": "Retail", "Fashion Photography": "Art",
  "Architecture": "Architecture", "Architecture & Design": "Architecture", "Interior Materials": "Architecture", "Real Estate": "Architecture",
  "Automotive": "Transportation", "Healthcare": "Medical", "Medical Education": "Education",
  "Fine Dining": "Hospitality", "Wine & Hospitality": "Hospitality",
};
export const briefCategory = (brief) => brief.category || SECTOR_CATEGORY[brief.sector] || "Display";
export const briefStatus = (brief) => brief.status || "FRONTIER";
export const briefNumber = (brief) => `Nº ${String(brief.id).padStart(3, "0")}`;
export const briefTags = (brief) => [brief.sector, ...(brief.keywords || [])].filter((t, i, a) => t && a.indexOf(t) === i).slice(0, 6);
